import { IonIcon } from '@ionic/react'
import { Close, PhoneAndroid, SupportAgent } from '@mui/icons-material'
import { Drawer, IconButton } from '@mui/material'
import { ModalOption } from '@vae/server/src/types/notifications'
import {
	bulb,
	chevronForwardOutline,
	closeSharp,
	documentText,
	flash,
	home,
	mail,
	receiptSharp,
	settings,
	statsChart,
	tv,
} from 'ionicons/icons'
import React, { useContext, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import packageJson from '../../../package.json'
import { AppContext } from '../../contexts/AppContext'
import useAppUpdates from '../../contexts/hooks/useAppUpdates'
import useUser from '../../contexts/hooks/useUser'
import { ModalContext } from '../../contexts/ModalContext'
import { ObjectContext } from '../../contexts/objectContext/ObjectContext'
import { UserContext } from '../../contexts/UserContext'
import ContractPicker from '../contractPicker/ContractPicker'
import LogoWithText from '../LogoWithText'

interface MenuItem {
	label: string
	icon: string
	activeModal?: ModalOption
	page?: string
	isRendered: boolean
	webOnly?: boolean
}

const Sidebar: React.FC = () => {
	const { insertModal } = useContext(ModalContext)
	const { skipLogin, logOut } = useContext(UserContext)
	const { hasElectricHeating, synerallContractId, hasObjects } = useContext(ObjectContext)
	const { menuOpen, toggleMenu, isMobileView, isNativePlatform } = useContext(AppContext)

	const { t } = useTranslation('components_swipeMenu')
	const menu = useRef<any>(null)
	const location = useLocation()
	const history = useHistory()

	const { updateAvailable, checkAndPromptUpdate } = useAppUpdates()

	const isActivePage = (page?: string) => {
		if (!page) return false
		return location.pathname.startsWith(`/${page}`)
	}

	const { user } = useUser()

	const renderMenuItems = (items: MenuItem[]) => {
		return items.map(item =>
			item.isRendered ? (
				<div
					className={`option tracking-wide cursor-pointer
                    transition-all duration-200 ease-linear
                    hover:bg-black/[0.02] dark:hover:bg-white/[0.02]
                    hover:border-l-4 hover:border-l-brand-primary-100-light/50 dark:hover:border-l-brand-primary-100-dark/50
                    ${
											isActivePage(item.page)
												? 'bg-black/[0.04] dark:bg-white/[0.04] border-l-4 border-l-brand-primary-100-light dark:border-l-brand-primary-100-dark'
												: ''
										}`}
					onClick={() => {
						if (isMobileView) toggleMenu()
						if (item.activeModal) {
							insertModal(item.activeModal)
						}
						if (item.page) {
							history.push(`/${item.page}`)
						}
					}}
					key={item.label}
				>
					<IonIcon
						icon={item.icon}
						className={`text-brand-primary-100-light dark:text-brand-primary-100-dark ${
							isActivePage(item.page) ? 'active' : ''
						}`}
					/>
					<span>{isActivePage(item.page) ? <strong>{item.label}</strong> : item.label}</span>
				</div>
			) : null
		)
	}

	const menuItems: MenuItem[] = useMemo(() => {
		return [
			{
				label: t('home'),
				icon: home,
				page: 'overview',
				isRendered: true,
				webOnly: true,
			},
			{
				label: t('electricity'),
				icon: flash,
				page: 'exchange',
				isRendered: true,
				webOnly: true,
			},
			{
				label: t('devices'),
				icon: tv,
				page: 'devices',
				isRendered: true,
				webOnly: true,
			},
			{
				label: t('saving'),
				icon: bulb,
				page: 'saving',
				isRendered: true,
				webOnly: true,
			},

			{
				label: t('sopimuksenTiedot'),
				icon: documentText,
				page: 'contract',
				isRendered: true,
			},
			{
				label: t('laskut'),
				icon: receiptSharp,
				page: 'invoices',
				isRendered: !!(user && synerallContractId),
			},
			{
				label: t('kuukausiraportit'),
				icon: statsChart,
				page: 'reports',
				isRendered: !!user,
			},
			{
				label: t('contact'),
				icon: mail,
				page: 'contact-support',
				isRendered: !!(user && synerallContractId),
			},
			{
				label: t('settings'),
				icon: settings,
				page: 'settings',
				isRendered: !!user,
			},
		]
	}, [t, hasElectricHeating, synerallContractId, skipLogin, location.pathname, user])

	const drawerContent = (
		<div
			id='sidebar'
			className='h-full overflow-auto bg-white text-dark dark:bg-background-200-dark dark:text-white border-r border-[#c3c3c3] dark:border-[#144448] flex flex-col'
		>
			<div
				id='header'
				className='sticky top-0 z-10 bg-white dark:bg-background-200-dark border-b border-[#c3c3c3] dark:border-[#144448] pl-[1.3rem] flex flex-row justify-between items-center h-[5.7rem]'
			>
				<LogoWithText size={48} />
				{isMobileView && (
					<IconButton onClick={toggleMenu}>
						<IonIcon className='text-text-grey-200 dark:text-text-grey-100' icon={closeSharp} />
					</IconButton>
				)}
			</div>

			<div className='flex flex-col min-h-0 flex-1'>
				<ContractPicker />
				{!isMobileView && (
					<div id='options' className='border-b border-[#c3c3c3] dark:border-[#144448]'>
						{renderMenuItems(menuItems.filter(item => item.webOnly))}
					</div>
				)}
				<div id='options'>{renderMenuItems(menuItems.filter(item => !item.webOnly))}</div>
				<div className='grow' />

				<div
					onClick={() => logOut()}
					className='text-center p-[0.6rem] ml-[0.7rem] mr-[0.7rem] mb-[0.7rem] rounded-[0.5rem] flex items-center justify-center gap-2 tracking-wide bg-[linear-gradient(135deg,_#2e7d32,_#43a047,_#66bb6a)] dark:bg-[linear-gradient(135deg,_#26a69a,_#22b194,_#30c9aa)]  text-white transition-all duration-300 hover:cursor-pointer hover:scale-[1.03] hover:shadow-lg hover:bg-[length:115%_100%]'
				>
					<Close className='text-[#cae3cc] text-2xl' />
					<span className='xl:text-[18px] lg:text-md'>{t('signOut')}</span>
				</div>
				{!isNativePlatform && (
					<div
						onClick={() => history.push('/application')}
						className='text-center p-[0.6rem] ml-[0.7rem] mr-[0.7rem] mb-[0.7rem] rounded-[0.5rem] flex items-center justify-center gap-2 tracking-wide bg-[linear-gradient(135deg,_#2e7d32,_#43a047,_#66bb6a)] dark:bg-[linear-gradient(135deg,_#26a69a,_#22b194,_#30c9aa)]  text-white transition-all duration-300 hover:cursor-pointer hover:scale-[1.03] hover:shadow-lg hover:bg-[length:115%_100%]'
					>
						<PhoneAndroid className='text-[#cae3cc] text-2xl' />
						<span className='xl:text-[18px] lg:text-md'>{t('downloadApp')}</span>
					</div>
				)}
				{!skipLogin && (
					<div id='contact'>
						<a
							onClick={() => history.push('/support')}
							className='text-center w-full p-[0.6rem] rounded-[0.5rem] hover flex items-center justify-center gap-2 tracking-wide bg-brand-primary-100-light text-white dark:bg-brand-primary-100-dark'
						>
							<SupportAgent className='text-[#cae3cc]' />
							<span className='xl:text-[14px] lg:text-sm'>{t('otaYhteytta')}</span>
						</a>
					</div>
				)}
				<div className='version-number'>
					<div>Älyenergia {packageJson.version} </div>
					{process.env.react_app_environment !== 'PRODUCTION' && (
						<div>
							<span>Server: {process.env.react_app_environment}</span>
						</div>
					)}

					{/* Only show checkVersion if updateAvailable */}
					{updateAvailable && (
						<div onClick={checkAndPromptUpdate} className='flex flex-row  gap-2 items-center'>
							{t('checkVersion')}
							<IonIcon icon={chevronForwardOutline} className='text-lg' />
						</div>
					)}
				</div>
			</div>
		</div>
	)

	return (
		<>
			<Drawer
				ref={menu}
				variant={isMobileView ? 'temporary' : 'permanent'}
				open={isMobileView ? menuOpen : true}
				onClose={toggleMenu}
				ModalProps={{
					keepMounted: true,
					container: document.querySelector('ion-app'),
				}}
				sx={{
					'& .MuiDrawer-paper': {
						width: {
							xs: '240px',
							sm: '260px',
							md: '280px',
							lg: '300px',
							xl: '320px',
						},
						borderRight: 'none',
						boxSizing: 'border-box',
					},
				}}
			>
				{drawerContent}
			</Drawer>
		</>
	)
}

export default Sidebar
